import { useCallback } from 'react';
import {
  parseEventParam,
  useEmitSiderRequestForeground,
} from '@robinpowered/dashboard-apps-common';
import {
  SHELL_AGENT_SIDER_ID,
  closeAgentChat,
  isAgentChatOpen,
  openAgentChat,
} from './agentChatParam';

/**
 * The nav button's "Assistant" action, and the ONE place the assistant asks
 * for the right-hand slot (direction 2 of the single-sider policy). Slot
 * requests are user-initiated by definition, and this click is the only way
 * a user opens the assistant, so arbitration lives here rather than in an
 * effect watching for the param to appear — param appearances that aren't
 * this click (a refresh restoring both params, a cross-app restore, a deep
 * link) mean the panel simply stays backgrounded under the composer, exactly
 * like an office sider would.
 *
 * Three states:
 *  - Closed → open; if the composer holds the slot, also ask it to yield.
 *  - Visible → close (plain param toggle).
 *  - Open but COVERED by the composer → ask it to yield; the param is
 *    already set, so the panel reveals the moment the composer closes.
 *
 * Yield requests run the composer's unsaved-changes guard: it closes (the
 * `event` param clears and the panel shows) or the user keeps editing, in
 * which case the composer emits foreground-denied and AgentChatSidebar drops
 * the panel's param — the standard pending-open drop every sider does.
 */
export const useToggleAgentChat = (): (() => void) => {
  const requestForeground = useEmitSiderRequestForeground();
  return useCallback(() => {
    const params = new URLSearchParams(window.location.search);
    const composerOpen = parseEventParam(params.get('event')).mode !== null;
    if (isAgentChatOpen(params.toString()) && !composerOpen) {
      closeAgentChat();
      return;
    }
    openAgentChat();
    if (composerOpen) {
      requestForeground(SHELL_AGENT_SIDER_ID);
    }
  }, [requestForeground]);
};
